<div class="detail-container" *ngIf="!isLoading && catch">

  <!-- Double hero: tactical left, context right -->
  <div class="map-hero">
    <div class="map-hero-cell" *ngIf="tacticalImageUrl" (click)="openLightbox(tacticalImageUrl, 'Tactical')" title="Click to enlarge">
      <img [src]="tacticalImageUrl" alt="Tactical map" class="map-hero-img" />
      <span class="map-hero-label">Tactical</span>
    </div>
    <div class="map-hero-cell map-hero-cell--placeholder" *ngIf="!tacticalImageUrl">
      <span>No tactical image</span>
    </div>

    <div class="map-hero-cell" *ngIf="contextImageUrl" (click)="openLightbox(contextImageUrl, 'Context')" title="Click to enlarge">
      <img [src]="contextImageUrl" alt="Context map" class="map-hero-img" />
      <span class="map-hero-label">Context</span>
    </div>
    <div class="map-hero-cell map-hero-cell--placeholder" *ngIf="!contextImageUrl">
      <span>No context image</span>
    </div>
  </div>

  <section class="catch-data-panel">
    <app-back-link link="/catches" label="Back to all catches"></app-back-link>

    <!-- Sequential navigation -->
    <nav class="catch-nav" *ngIf="listSize > 0">
      <button mat-stroked-button [disabled]="!prevId" (click)="prevId && navigateTo(prevId)">← Prev</button>
      <span class="catch-position">{{ currentIndex + 1 }} / {{ listSize }}</span>
      <button mat-stroked-button [disabled]="!nextId" (click)="nextId && navigateTo(nextId)">Next →</button>
    </nav>

    <h2 class="catch-title">Catch #{{ catch.catchId }}</h2>

    <dl class="catch-metadata">
      <dt>Fish Type</dt>
      <dd>
        <span class="fish-type-dot" [style.background-color]="catch.fish_type_display_color"></span>
        {{ catch.fish_type_full_name }}
      </dd>

      <dt>Date &amp; Time</dt>
      <dd>{{ catch.time | date:'full' }}</dd>

      <dt>Tide Cycle</dt>
      <dd>{{ catch.tide_cycle ?? 'N/A' }}</dd>

      <dt>Air Temperature</dt>
      <dd>{{ catch.temperature !== null ? (catch.temperature + '°F') : 'N/A' }}</dd>

      <dt>Water Temperature</dt>
      <dd>
        {{ catch.water_temperature !== null ? (catch.water_temperature + '°F') : 'N/A' }}
        <span *ngIf="catch.water_temperature_trend"> ({{ catch.water_temperature_trend }})</span>
      </dd>

      <dt>Wind</dt>
      <dd>{{ catch.wind_speed ?? 'N/A' }}{{ catch.wind_speed ? ' mph' : '' }} {{ catch.wind_direction ?? '' }}</dd>

      <dt>Forecast</dt>
      <dd>{{ catch.short_forecast ?? 'N/A' }}</dd>

      <dt>GPS Latitude</dt>
      <dd>{{ catch.lat | number:'1.6-6' }}</dd>

      <dt>GPS Longitude</dt>
      <dd>{{ catch.lng | number:'1.6-6' }}</dd>
    </dl>

    <div id="catch-location-map" class="catch-location-map"></div>
    <div class="location-edit-controls">
      <ng-container *ngIf="!isEditingLocation">
        <button mat-stroked-button (click)="startEditLocation()">Edit Location</button>
      </ng-container>
      <ng-container *ngIf="isEditingLocation">
        <p class="edit-hint">Drag the red pin to the correct location</p>
        <button mat-stroked-button (click)="saveLocation()" [disabled]="isSavingLocation">
          {{ isSavingLocation ? 'Saving…' : 'Save Location' }}
        </button>
        <button mat-stroked-button (click)="cancelEditLocation()" [disabled]="isSavingLocation">Cancel</button>
      </ng-container>
    </div>

    <!-- Angler notes -->
    <h3 class="section-label">Angler Notes</h3>
    <app-resizable-textarea
      [(ngModel)]="userNotes"
      placeholder="Optional — add context the satellite image can't show: leeward side of sandbar, fishing with soft plastics, bait ball nearby, etc. Included in re-analysis."
      [minRows]="1">
    </app-resizable-textarea>
    <div class="notes-actions">
      <button mat-stroked-button (click)="saveNotes()" [disabled]="isSavingNotes">
        {{ isSavingNotes ? 'Saving…' : 'Save Notes' }}
      </button>
    </div>

    <!-- Map / Analyze / Delete actions -->
    <div class="action-row" *ngIf="catch.fish_type !== 'location'">
      <button mat-stroked-button (click)="triggerGetMaps()" [disabled]="isGettingMaps || isAnalyzing"
        title="Fetch tactical and context satellite imagery for this catch location from Google Maps">
        <span *ngIf="!isGettingMaps">{{ tacticalImageUrl ? 'Refresh Maps' : 'Get Maps' }}</span>
        <span *ngIf="isGettingMaps">Getting Maps…</span>
      </button>
      <button mat-stroked-button (click)="triggerAnalysis()" [disabled]="isAnalyzing || isGettingMaps"
        title="Fetch satellite maps and run AI vision analysis to identify the fishing location, structure, and depth">
        <span *ngIf="!isAnalyzing">{{ locationAnalysis ? 'Re-analyze Location' : 'Analyze Location' }}</span>
        <span *ngIf="isAnalyzing">Analyzing…</span>
      </button>
    </div>
    <div class="action-row">
      <button mat-stroked-button class="delete-btn" (click)="deleteCatch()" [disabled]="isDeleting">
        {{ isDeleting ? 'Deleting…' : 'Delete Catch' }}
      </button>
    </div>

    <!-- Location Analysis -->
    <ng-container *ngIf="locationAnalysis">
      <h3 class="section-label">Location Analysis</h3>
      <dl class="catch-metadata">

        <dt>Named Location</dt>
        <dd>
          <ng-container *ngIf="editingField !== 'named_location'; else editNamedLocation">
            {{ locationAnalysis.named_location ?? 'N/A' }}
            <button class="edit-btn" (click)="startEdit('named_location', locationAnalysis.named_location)">✎</button>
          </ng-container>
          <ng-template #editNamedLocation>
            <input class="edit-input" [(ngModel)]="editValue" />
            <button class="save-btn" (click)="saveEdit('named_location')">Save</button>
            <button class="cancel-btn" (click)="cancelEdit()">Cancel</button>
          </ng-template>
        </dd>

        <dt>Feature</dt>
        <dd>
          <ng-container *ngIf="editingField !== 'confirmed_feature'; else editFeature">
            {{ locationAnalysis.confirmed_feature ?? 'N/A' }}
            <button class="edit-btn" (click)="startEdit('confirmed_feature', locationAnalysis.confirmed_feature)">✎</button>
          </ng-container>
          <ng-template #editFeature>
            <input class="edit-input" [(ngModel)]="editValue" />
            <button class="save-btn" (click)="saveEdit('confirmed_feature')">Save</button>
            <button class="cancel-btn" (click)="cancelEdit()">Cancel</button>
          </ng-template>
        </dd>

        <dt>Material</dt>
        <dd>
          <ng-container *ngIf="editingField !== 'material'; else editMaterial">
            {{ locationAnalysis.material ?? 'N/A' }}
            <button class="edit-btn" (click)="startEdit('material', locationAnalysis.material)">✎</button>
          </ng-container>
          <ng-template #editMaterial>
            <input class="edit-input" [(ngModel)]="editValue" />
            <button class="save-btn" (click)="saveEdit('material')">Save</button>
            <button class="cancel-btn" (click)="cancelEdit()">Cancel</button>
          </ng-template>
        </dd>

        <dt>Depth</dt>
        <dd>
          <ng-container *ngIf="editingField !== 'depth_category'; else editDepth">
            {{ locationAnalysis.depth_category ?? 'N/A' }}
            <button class="edit-btn" (click)="startEdit('depth_category', locationAnalysis.depth_category)">✎</button>
          </ng-container>
          <ng-template #editDepth>
            <input class="edit-input" [(ngModel)]="editValue" />
            <button class="save-btn" (click)="saveEdit('depth_category')">Save</button>
            <button class="cancel-btn" (click)="cancelEdit()">Cancel</button>
          </ng-template>
        </dd>

        <dt>Confidence</dt>
        <dd>
          <ng-container *ngIf="editingField !== 'confidence_score_0_10'; else editConfidence">
            <span class="confidence-score">{{ locationAnalysis.confidence_score_0_10 }}/10</span>
            <span class="confidence-bar">
              <span class="confidence-fill" [style.width.%]="(locationAnalysis.confidence_score_0_10 ?? 0) * 10"></span>
            </span>
            <button class="edit-btn" (click)="startEdit('confidence_score_0_10', locationAnalysis.confidence_score_0_10)">✎</button>
          </ng-container>
          <ng-template #editConfidence>
            <input class="edit-input edit-input--short" type="number" min="0" max="10" [(ngModel)]="editValue" />
            <button class="save-btn" (click)="saveEdit('confidence_score_0_10')">Save</button>
            <button class="cancel-btn" (click)="cancelEdit()">Cancel</button>
          </ng-template>
        </dd>

        <dt>Pattern Insight</dt>
        <dd>
          <ng-container *ngIf="editingField !== 'pattern_insight'; else editInsight">
            <span class="insight-text">{{ locationAnalysis.pattern_insight ?? 'N/A' }}</span>
            <button class="edit-btn" (click)="startEdit('pattern_insight', locationAnalysis.pattern_insight)">✎</button>
          </ng-container>
          <ng-template #editInsight>
            <textarea class="edit-input edit-input--textarea" [(ngModel)]="editValue" cdkTextareaAutosize cdkAutosizeMinRows="1"></textarea>
            <div class="edit-actions">
              <button class="save-btn" (click)="saveEdit('pattern_insight')">Save</button>
              <button class="cancel-btn" (click)="cancelEdit()">Cancel</button>
            </div>
          </ng-template>
        </dd>

      </dl>
    </ng-container>

  </section>
</div>

<!-- Lightbox -->
<div class="lightbox" *ngIf="lightboxUrl" (click)="closeLightbox()">
  <div class="lightbox-inner" (click)="$event.stopPropagation()">
    <button class="lightbox-close" (click)="closeLightbox()">✕</button>
    <p class="lightbox-label">{{ lightboxLabel }}</p>
    <img [src]="lightboxUrl" class="lightbox-img" />
  </div>
</div>

<app-loading-spinner [isLoading]="isLoading"></app-loading-spinner>

<div class="error-panel" *ngIf="!isLoading && errorMessage">
  <p>{{ errorMessage }}</p>
  <a routerLink="/catches">← Return to all catches</a>
</div>
